/**
 * apps/command/src/screens/BulkScreen.tsx — §16 screen 15, §4.8's bulk
 * operations and §10's two-person rule.
 *
 * ============================================================================
 * THE PREVIEW IS THE SCREEN. THE EXECUTE BUTTON IS A DETAIL.
 * ============================================================================
 *
 * §4.8: "select many kiosks → preview exactly what will happen to each →
 * execute with a live progress list". Almost every bulk tool ever built inverts
 * that: a big list, a big button, and a modal that says "are you sure?" — which
 * is a question nobody has ever answered honestly at 03:00.
 *
 * So this screen never shows an execute control on the same view as the
 * selection. Making a plan is one action, and the result is a page of verdicts:
 * this kiosk will reboot, this one will not because a technician has it open,
 * this one will not because the same command is already queued there. Only then
 * is there a button, and it asks for the count to be typed.
 *
 * ============================================================================
 * IT SAYS WHO CAN AND CANNOT APPROVE, BEFORE THEY TRY.
 * ============================================================================
 *
 * §10's rule is that a second PERSON approves. The issuer looking at their own
 * plan sees the reason they cannot approve it stated on the page rather than
 * discovering it from a 422 — the rule is not a trap, it is a policy, and a
 * policy that only announces itself when broken reads as a bug.
 *
 * There is no control anywhere on this screen that skips the preview, skips the
 * approval, or forces a kiosk that the plan skipped. Each of those would be the
 * one control that makes the rest decorative.
 */
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { bulkRequiresTwoPerson, type KioskCommand } from '@navoiy/domain';
import type { components } from '@navoiy/api-types';
import { api, ApiError, unwrap } from '../api/client.js';
import { useSession } from '../auth/SessionContext.js';
import { useFleet } from '../realtime/useFleet.js';
import { hasKey, t } from '../i18n/index.js';
import { stamp } from '../lib/format.js';
import {
  Button,
  EmptyState,
  Field,
  Notice,
  Panel,
  PanelHeader,
  Skeleton,
  TextInput,
} from '../ui/index.jsx';
import { KpiCard, PageHeader } from '../ui/console.jsx';
import { IconBack } from '../ui/icons.jsx';

type MbtBulkPlan = components['schemas']['MbtBulkPlan'];

/**
 * The verbs offered here, and WHY THIS LIST IS SHORTER than the kiosk detail's.
 *
 * `diagnostics.run` is missing on purpose: it is a per-kiosk investigation, and
 * a fleet-wide diagnostics sweep is 35 log bundles nobody will read. Offering
 * it would be offering busywork that looks like action.
 *
 * `update.install` IS here, and it is the one card the Channels screen's
 * «Yangilanishni yuborish» button links to (`?type=update.install`, read
 * below). It already sits in `BULK_TWO_PERSON_COMMANDS`, so a fleet-wide
 * push gets the same second-approver rule as a fleet-wide reboot — nothing
 * about arriving from that button skips it.
 */
const OFFERED: readonly KioskCommand[] = [
  'content.refresh',
  'update.install',
  'app.restart',
  'machine.reboot',
];

/** A `KioskCommand` this screen is willing to preselect from the URL. */
function isOffered(value: string | null): value is KioskCommand {
  return OFFERED.includes(value as KioskCommand);
}

function typeLabel(type: string): string {
  return hasKey(`command.type.${type}`) ? t(`command.type.${type}`) : type;
}

/** What the command actually does to a kiosk, in one sentence. */
function commandEffect(type: string): string {
  return hasKey(`command.effect.${type}`) ? t(`command.effect.${type}`) : '';
}

function skipLabel(reason: string): string {
  return hasKey(`bulk.skipReason.${reason}`) ? t(`bulk.skipReason.${reason}`) : reason;
}

function statusLabel(status: string): string {
  return hasKey(`command.status.${status}`) ? t(`command.status.${status}`) : status;
}

export function BulkScreen(): React.JSX.Element {
  /*
    HOW MANY MACHINES THIS WILL TOUCH, live.

    The composer said «Barcha faol kiosklar» and nothing else, so an operator
    pressed «Koʻrib chiqish» without ever seeing a number — and the number is
    the whole weight of the decision. Four is a test bench; thirty-five is the
    province. It comes from the same fleet feed the grid uses rather than from a
    count typed here, so it cannot drift.
  */
  const { expire, allows } = useSession();
  const onExpired = useCallback(() => expire(), [expire]);
  const mayIssue = allows('mbt.command.issue');
  const fleet = useFleet(onExpired);
  const targetCount = fleet.kiosks.length;
  const [params, setParams] = useSearchParams();
  const openId = params.get('plan');

  const [plans, setPlans] = useState<MbtBulkPlan[] | null>(null);
  const [plan, setPlan] = useState<MbtBulkPlan | null>(null);
  /**
   * `?type=` PICKS THE INITIAL CARD, ONCE. Read at construction time rather
   * than in an effect — the Channels screen's «Yangilanishni yuborish» button
   * arrives as `/bulk?type=update.install` and the operator should land with
   * that card already selected, not watch it jump there a frame later. It is
   * still one click from any other verb: this is a default, not a lock.
   */
  const [type, setType] = useState<KioskCommand>(() => {
    const requested = new URLSearchParams(window.location.search).get('type');
    return isOffered(requested) ? requested : 'content.refresh';
  });
  const [reason, setReason] = useState('');
  const [confirmCount, setConfirmCount] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const loadList = useCallback(async () => {
    try {
      const data = unwrap(await api().GET('/mbt/fleet/bulk/plans'));
      setPlans(data.items as MbtBulkPlan[]);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onExpired();
      else setError(t('error.network'));
    }
  }, [onExpired]);

  const loadPlan = useCallback(
    async (id: string) => {
      try {
        const data = unwrap(
          await api().GET('/mbt/fleet/bulk/plans/{plan_id}', {
            params: { path: { plan_id: id } },
          }),
        );
        setPlan(data as MbtBulkPlan);
      } catch (caught) {
        if (caught instanceof ApiError && caught.status === 401) onExpired();
        else if (caught instanceof ApiError && caught.status === 404) setParams({}, { replace: true });
        else setError(t('error.network'));
      }
    },
    [onExpired, setParams],
  );

  useEffect(() => {
    if (openId) void loadPlan(openId);
    else {
      setPlan(null);
      void loadList();
    }
  }, [openId, loadPlan, loadList]);

  /**
   * An EXECUTED plan is polled; a pending one is not.
   *
   * §4.8's "live progress list" is about commands moving queued → delivered →
   * deferred → acked without anybody clicking. A plan awaiting approval has
   * nothing that moves on its own, and polling it would be 35 pointless
   * requests a minute during an incident.
   */
  useEffect(() => {
    if (!openId || !plan?.executed) return undefined;
    const timer = setInterval(() => void loadPlan(openId), 5_000);
    return () => clearInterval(timer);
  }, [openId, plan?.executed, loadPlan]);

  async function create(): Promise<void> {
    if (!reason.trim()) {
      setError(t('bulk.reasonRequired'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const created = unwrap(
        await api().POST('/mbt/fleet/bulk/plans', {
          body: { type, reason: reason.trim(), targets: { all: true } },
        }),
      ) as MbtBulkPlan;
      setReason('');
      setParams({ plan: created.id }, { replace: false });
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onExpired();
      else if (caught instanceof ApiError && caught.status === 403) {
        setError(t('bulk.noPermission'));
      } else if (caught instanceof ApiError && caught.status === 422) {
        setError(t('bulk.nothingToDo'));
      } else setError(t('bulk.createFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function approve(): Promise<void> {
    if (!plan) return;
    setBusy(true);
    setError(null);
    try {
      await api().POST('/mbt/fleet/bulk/plans/{plan_id}/approve', {
        params: { path: { plan_id: plan.id } },
      });
      await loadPlan(plan.id);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onExpired();
      else if (caught instanceof ApiError && caught.status === 422) {
        setError(t('bulk.cannotApproveOwn'));
      } else setError(t('bulk.approveFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function execute(): Promise<void> {
    if (!plan) return;
    if (Number(confirmCount) !== plan.target_count) {
      setError(t('bulk.confirmCountMismatch'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api().POST('/mbt/fleet/bulk/plans/{plan_id}/execute', {
        params: { path: { plan_id: plan.id } },
        body: { confirm_count: plan.target_count },
      });
      setConfirmCount('');
      await loadPlan(plan.id);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onExpired();
      else setError(t('bulk.executeFailed'));
    } finally {
      setBusy(false);
    }
  }

  // -- one plan ---------------------------------------------------------------
  if (openId) {
    if (!plan) return <Skeleton className="h-64" />;

    /**
     * WHETHER THIS PERSON MAY APPROVE, decided on the page rather than by a 422.
     *
     * The issuer is the one case §10's rule exists for, so it is the one case
     * the screen explains: the button is absent and a sentence says why. A rule
     * that only announces itself when broken reads as a bug.
     *
     * `created_by_me` comes from the SERVER, computed from the session's id.
     * The obvious client-side version — comparing `created_by_name` to the
     * signed-in user's name — is wrong for any two staff who share a name, and
     * it is wrong in the dangerous direction: it would offer the issuer an
     * approve button.
     */
    const isMine = plan.created_by_me;
    const expired = new Date(plan.expires_at).getTime() <= Date.now();
    /**
     * `requires_approval` is part of this, and leaving it out was the bug: a
     * plan that needs no second person would still show an approve button to a
     * colleague, which quietly teaches everyone that approving is a formality
     * that appears on everything.
     */
    const mayApprove =
      mayIssue && plan.requires_approval && !isMine && !plan.approved && !plan.executed && !expired;
    const mayExecute =
      mayIssue && !plan.executed && !expired && (!plan.requires_approval || plan.approved);

    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => setParams({}, { replace: true })}
          className="flex items-center gap-1.5 text-micro text-ink-500 transition-colors duration-state hover:text-ink-300"
        >
          <IconBack className="h-4 w-4" />
          {t('bulk.back')}
        </button>

        {error ? <Notice tone="warn">{error}</Notice> : null}

        <Panel>
          <PanelHeader
            title={typeLabel(plan.command)}
            aside={t('bulk.targetCount', { n: plan.target_count })}
          />
          <div className="space-y-3 px-4 py-4">
            <dl className="divide-y divide-hairline text-label">
              <div className="flex justify-between py-1.5">
                <dt className="text-ink-500">{t('bulk.reason')}</dt>
                <dd className="text-ink-100">{plan.reason}</dd>
              </div>
              <div className="flex justify-between py-1.5">
                <dt className="text-ink-500">{t('bulk.newPlan')}</dt>
                <dd className="text-ink-100">{plan.created_by_name}</dd>
              </div>
              <div className="flex justify-between py-1.5">
                <dt className="text-ink-500">{t('bulk.expiresAt')}</dt>
                <dd className="tabular-nums text-ink-100">
                  {expired ? t('bulk.expired') : stamp(new Date(plan.expires_at))}
                </dd>
              </div>
              {plan.approved_by_name ? (
                <div className="flex justify-between py-1.5">
                  <dt className="text-ink-500">{t('bulk.approve')}</dt>
                  <dd className="text-ok">
                    {t('bulk.approvedBy', { name: plan.approved_by_name })}
                  </dd>
                </div>
              ) : null}
              {plan.executed_by_name ? (
                <div className="flex justify-between py-1.5">
                  <dt className="text-ink-500">{t('bulk.execute')}</dt>
                  <dd className="text-ink-100">
                    {t('bulk.executedBy', { name: plan.executed_by_name })}
                    {plan.issued_count !== undefined
                      ? ` · ${t('bulk.issued', { n: plan.issued_count })}`
                      : ''}
                  </dd>
                </div>
              ) : null}
            </dl>

            {/* §10's rule, stated as policy before anybody trips over it. */}
            {plan.requires_approval && !plan.approved ? (
              <Notice tone="warn">
                {t('bulk.requiresApproval')} — {t('bulk.requiresApprovalNote')}
              </Notice>
            ) : null}
          </div>
        </Panel>

        {/* The preview: every selected kiosk and its verdict. This is the thing
            the approver is approving, so it is on the page they approve from. */}
        <Panel>
          <PanelHeader title={t('bulk.preview')} aside={t('bulk.previewNote')} />
          <ul className="divide-y divide-hairline px-4 py-2">
            {plan.preview.map((entry) => (
              <li key={entry.device_id} className="flex items-center justify-between gap-4 py-2">
                <div className="min-w-0">
                  <p className="text-label">
                    <span
                      className={`font-medium tabular-nums ${
                        entry.will_run ? 'text-ink-100' : 'text-ink-500'
                      }`}
                    >
                      {entry.short_code}
                    </span>{' '}
                    <span className="text-ink-500">{entry.mahalla_name}</span>
                  </p>
                </div>
                <span
                  className={`shrink-0 text-micro ${entry.will_run ? 'text-ok' : 'text-ink-500'}`}
                >
                  {entry.will_run ? t('bulk.willRun') : skipLabel(entry.skip_reason ?? '')}
                </span>
              </li>
            ))}
          </ul>
        </Panel>

        {mayApprove ? (
          <Panel>
            <div className="px-4 py-4">
              <Button onClick={() => void approve()} disabled={busy}>
                {t('bulk.approve')}
              </Button>
            </div>
          </Panel>
        ) : null}

        {/* The issuer is told WHY there is no approve button, rather than
            finding out by pressing one that 422s. */}
        {mayIssue && isMine && plan.requires_approval && !plan.approved && !plan.executed ? (
          <Notice tone="quiet">{t('bulk.cannotApproveOwn')}</Notice>
        ) : null}

        {mayExecute ? (
          <Panel>
            <PanelHeader title={t('bulk.execute')} />
            <div className="space-y-3 px-4 py-4">
              <Field
                label={t('bulk.confirmCount')}
                hint={t('bulk.confirmCountHint', { n: plan.target_count })}
              >
                <TextInput
                  inputMode="numeric"
                  value={confirmCount}
                  onChange={(event) => setConfirmCount(event.target.value)}
                />
              </Field>
              <Button onClick={() => void execute()} disabled={busy}>
                {t('bulk.execute')}
              </Button>
            </div>
          </Panel>
        ) : null}

        {plan.executed ? (
          <Panel>
            <PanelHeader
              title={t('bulk.progress')}
              aside={
                plan.skipped && plan.skipped.length > 0
                  ? t('bulk.skippedAtExecute', { n: plan.skipped.length })
                  : undefined
              }
            />
            <ul className="divide-y divide-hairline px-4 py-2">
              {(plan.progress ?? []).map((command) => (
                <li key={command.id} className="flex items-center justify-between gap-4 py-2">
                  <span className="text-label font-medium tabular-nums text-ink-100">
                    {command.short_code}
                  </span>
                  <span
                    className={`shrink-0 text-micro ${
                      command.status === 'failed' || command.status === 'expired'
                        ? 'text-crit'
                        : command.status === 'deferred'
                          ? 'text-warn'
                          : command.status === 'acked'
                            ? 'text-ok'
                            : 'text-ink-500'
                    }`}
                  >
                    {statusLabel(command.status)}
                  </span>
                </li>
              ))}
            </ul>
          </Panel>
        ) : null}
      </div>
    );
  }

  // -- the queue --------------------------------------------------------------
  const queue = plans ?? [];
  const awaiting = queue.filter((row) => row.requires_approval && !row.approved && !row.executed);
  const ready = queue.filter((row) => (row.approved || !row.requires_approval) && !row.executed);
  const executed = queue.filter((row) => row.executed);
  const reach = queue
    .filter((row) => !row.executed)
    .reduce((sum, row) => sum + row.target_count, 0);

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow={<span>{t('bulk.eyebrow')}</span>}
        title={t('nav.bulk')}
        subtitle={t('bulk.subtitle')}
        right={
          <p className="text-hero font-bold tabular-nums leading-none text-ink-100">
            {awaiting.length}
          </p>
        }
      />

      {error ? <Notice tone="warn">{error}</Notice> : null}

      {/*
        §10's two-person rule, as four numbers.

        «Awaiting» leads because it is the only one that is somebody's job right
        now — a plan nobody approves silently expires, and an expiry looks
        exactly like a decision not to run until you read the reason. «Reach» is
        the count of kiosks the UNEXECUTED plans would touch, which is the
        number an approver should have in their head before they press anything.
      */}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label={t('bulk.kpiAwaiting')}
          value={String(awaiting.length)}
          absent={t('app.notYet')}
          tone={awaiting.length > 0 ? 'warn' : 'ok'}
          footerLabel={t('bulk.kpiAwaitingMeaning')}
          footerValue=""
        />
        <KpiCard
          label={t('bulk.kpiReady')}
          value={String(ready.length)}
          absent={t('app.notYet')}
          tone={ready.length > 0 ? 'info' : 'neutral'}
          footerLabel={t('bulk.kpiPlans')}
          footerValue={String(queue.length)}
        />
        <KpiCard
          label={t('bulk.kpiReach')}
          value={String(reach)}
          absent={t('app.notYet')}
          spread={queue.filter((row) => !row.executed).map((row) => row.target_count)}
          tone="neutral"
          footerLabel={t('bulk.kpiReachMeaning')}
          footerValue=""
        />
        <KpiCard
          label={t('bulk.kpiExecuted')}
          value={String(executed.length)}
          absent={t('app.notYet')}
          tone="ok"
          footerLabel={t('bulk.kpiExecutedMeaning')}
          footerValue=""
        />
      </div>

      {mayIssue ? (
        <Panel>
          <PanelHeader title={t('bulk.newPlan')} aside={t('bulk.subtitle')} />
          <div className="space-y-5 px-4 py-4">
            {/*
              ==================================================================
              THE COMMAND PICKER: THREE CARDS, NOT THREE PILLS OF ENUM.
              ==================================================================

              It was three small buttons reading «content.refresh»,
              «app.restart», «machine.reboot» — the raw identifiers, in English,
              on a Uzbek-only console, with a single line underneath saying
              whether approval was needed for whichever happened to be selected.

              An operator picking one of these is deciding what happens to every
              kiosk in the province at once. What they need beside each choice
              is what it DOES and how disruptive it is, not its wire format. So
              each is a card with its name, its consequence in one sentence, and
              its approval requirement stated ON the card rather than a line
              below that changes as you click.

              Approval is shown per option and always — an operator should be
              able to see that «machine.reboot» needs a second person BEFORE
              choosing it, which is the entire point of §10's rule.
            */}
            <fieldset>
              <legend className="mb-2 text-label text-ink-300">{t('bulk.command')}</legend>
              <div className="grid gap-3 sm:grid-cols-3">
                {OFFERED.map((command) => {
                  const chosen = command === type;
                  const twoPerson = bulkRequiresTwoPerson(command);
                  return (
                    <button
                      key={command}
                      type="button"
                      aria-pressed={chosen}
                      onClick={() => setType(command)}
                      className={`flex flex-col gap-1.5 rounded-card border p-3 text-left transition-shadow duration-state ${
                        chosen
                          ? 'border-brand bg-panel shadow-lift'
                          : 'border-hairline bg-panel shadow-card hover:shadow-lift'
                      }`}
                    >
                      <span className="flex items-start justify-between gap-2">
                        <span className="text-label font-semibold text-ink-100">
                          {typeLabel(command)}
                        </span>
                        {chosen ? (
                          <span className="mt-0.5 h-2 w-2 shrink-0 rounded-full bg-brand" />
                        ) : null}
                      </span>
                      <span className="text-micro leading-snug text-ink-500">
                        {commandEffect(command)}
                      </span>
                      <span
                        className={`mt-auto text-nano uppercase tracking-wide ${
                          twoPerson ? 'text-warn' : 'text-ink-500'
                        }`}
                      >
                        {twoPerson ? t('bulk.needsTwo') : t('bulk.needsOne')}
                      </span>
                    </button>
                  );
                })}
              </div>
            </fieldset>

            {/* Every active kiosk, and nothing else — see `bulk.targetsOnly`.
                The API takes an explicit id list; the UI to build one belongs on
                §16 screen 5's grid, where the tiles are. */}
            <Field label={t('bulk.targets')} hint={t('bulk.targetsOnly')}>
              <div className="flex items-baseline gap-2">
                <span className="text-figure font-bold tabular-nums leading-none text-ink-100">
                  {targetCount}
                </span>
                <span className="text-label text-ink-300">{t('bulk.targetsAll')}</span>
              </div>
            </Field>

            <Field label={t('bulk.reason')} hint={t('bulk.reasonHint')}>
              <TextInput
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder={t('bulk.reasonPlaceholder')}
              />
            </Field>

            {/*
              THE SENTENCE, before the button. «Restart the app on 34 kiosks —
              a second person must approve» is the whole plan in one line, and
              it is the last thing between an operator and a province-wide
              action. It reads from the same three values the request will
              carry, so it cannot describe something else.
            */}
            <div className="rounded-card border border-hairline bg-panel-raised px-3 py-2">
              <p className="text-micro leading-relaxed text-ink-300">
                {t('bulk.summary', { command: typeLabel(type), n: String(targetCount) })}
                {bulkRequiresTwoPerson(type) ? ` · ${t('bulk.requiresApproval')}` : ''}
              </p>
            </div>

            <Button onClick={() => void create()} disabled={busy}>
              {t('bulk.preview')}
            </Button>
          </div>
        </Panel>
      ) : null}

      <Panel>
        <PanelHeader title={t('bulk.title')} aside={t('bulk.pending')} />
        <div className="px-4 py-3">
          {plans === null ? (
            <Skeleton className="h-24" />
          ) : plans.length === 0 ? (
            <EmptyState message={t('bulk.empty')} />
          ) : (
            <ul className="divide-y divide-hairline">
              {plans.map((entry) => (
                <li key={entry.id}>
                  <button
                    type="button"
                    onClick={() => setParams({ plan: entry.id })}
                    className="flex w-full items-center justify-between gap-4 py-2.5 text-left transition-colors duration-state hover:text-brand"
                  >
                    <div className="min-w-0">
                      <p className="text-label text-ink-100">{typeLabel(entry.command)}</p>
                      <p className="text-micro text-ink-500">
                        {entry.created_by_name} · {t('bulk.targetCount', { n: entry.target_count })}
                      </p>
                    </div>
                    <span
                      className={`shrink-0 text-micro ${
                        entry.requires_approval && !entry.approved ? 'text-warn' : 'text-ink-500'
                      }`}
                    >
                      {entry.requires_approval && !entry.approved
                        ? t('bulk.awaitingApproval')
                        : t('bulk.willRun')}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Panel>
    </div>
  );
}
